const queryKeys = new Set()
const paramsKeys = new Set()

function nullifyMissing(prevKeysSet, nextObj = {}) {
    const nulls = {}
    prevKeysSet.forEach(k => {
        if (!(k in nextObj)) nulls[k] = undefined
    })
    prevKeysSet.clear()
    Object.keys(nextObj).forEach(k => prevKeysSet.add(k))
    return nulls
}

export default function getAnalytics({ dlName, wapp } = {}) {

    function createUser(p) {
        try {
            const { res, user = res?.wappResponse.store.getState('req.user') } = p
            return {
                user: {
                    _id: user?._id || null,
                    userType: user?.userType || null,
                    email: user?.email || null
                }
            }
        } catch (e) {
            console.log(e)
            return {
                user: {}
            }
        }
    }

    function createLocation({ req, res }) {

        try {
            const location = {
                url: req.url,
                path: req.path,
                requestPath: res.wappResponse.route.requestPath,
                query: req.wappRequest.query || {},
                params: res.wappResponse.route.params || {},
                history: {
                    action: req.history.action || 'PUSH',
                    state: req.history.state || {}
                },
                statusCode: res.statusCode
            }

            const { params = {}, query = {} } = location

            const queryNulls = nullifyMissing(queryKeys, query)
            const paramsNulls = nullifyMissing(paramsKeys, params)

            return {
                location: {
                    ...location,
                    query: { ...queryNulls, ...query },
                    params: { ...paramsNulls, ...params }
                }
            }
        } catch (e) {
            console.log(e)
            return {
                location: {}
            }
        }
    }

    function push({ obj, req, res }) {
        try {
            if (typeof window !== 'undefined' && window.dlPush) {
                if (wapp.globals.DEV) {
                    console.log('[GTM]', obj.event, obj)
                }
                window.dlPush(dlName, {
                    ...!obj?.user ? createUser({ req, res }) : {},
                    ...!obj?.location ? createLocation({ req, res }) : {},
                    ...obj ? obj : {}
                })
            }
        } catch (e) {
            console.log(e)
        }
    }

    return {
        consent: (p) => {
            try {
                const { event, consent, req, res } = p
                push({
                    obj: {
                        event,
                        consent
                    },
                    req,
                    res
                })
            } catch (e) {
                console.log(e)
            }
        },
        user: (p) => {
            try {
                const { user, req, res } = p
                push({
                    obj: {
                        event: 'user_change',
                        ...createUser({ user })
                    },
                    req,
                    res
                })
            } catch (e) {
                console.log(e)
            }
        },
        location: (p) => {
            try {
                const { req, res } = p
                push({
                    obj: {
                        event: 'location_change',
                        ...createLocation({ req, res })
                    },
                    req,
                    res
                })
            } catch (e) {
                console.log(e)
            }
        },
        response: (p) => {

            try {

                const { requestParams } = p
                const { req, res, requestName = '', args = {} } = requestParams

                const enableRequests = ['applyNew', 'userSignup', 'userSaveUserType', 'jobNew', 'jobSaveStep1', 'jobSaveStep2', 'jobSaveStep3', 'jobActivate']

                if (!enableRequests.includes(requestName)) {
                    return
                }

                const response = p.response && p.response[requestName] ? p.response[requestName] : p.response

                const ok = !!(response?.items || response?._id || response?.record?._id || response.pendingModificationsId)

                if (!ok) {
                    return
                }

                const pJob = res.wappResponse.store.getState('res.responses.jobSaveStep3.record') || res.wappResponse.store.getState('res.responses.jobFindById')
                const job = pJob?._id === args?._id ? pJob : null

                push({
                    obj: {
                        event: 'response_' + (requestParams?.requestName),
                        response: {
                            requestName,
                            ok
                        },
                        ...requestName.match('job') ? {
                            job: {
                                _id: response?._id || response?.record?._id || args?._id || null,
                                title: response?.title || response?.record?.title || job?.title || null,
                                position: response?.position?.slug || response?.record?.position?.slug || job?.position?.slug || null,
                                pendingModificationsId: response.pendingModificationsId || null
                            }
                        } : {},
                        ...requestName.match('apply') ? {
                            apply: {
                                _id: response?._id || response?.record?._id || null
                            },
                            job: {
                                _id: response?.job?._id || response?.record?.job?._id || null,
                                title: response?.job?.title || response?.record?.job?.title || null,
                                position: response?.job?.position?.slug || response?.record?.job?.position?.slug || null
                            }
                        } : {
                            apply: null
                        },
                        ...!requestName.match('apply') && !requestName.match('job') ? {
                            job: null
                        } : {}
                    },
                    req,
                    res
                })

            } catch (e) {
                console.log(e)
            }

        },
        ecommerce: (p) => {
            try {
                const { req, res, event, items, customer, total, ecommerce } = p

                const cartItems = typeof items === 'string' ? JSON.parse(items) : (items || [])
                // noinspection JSUnusedLocalSymbols
                const value = typeof total === 'number'
                    ? total
                    : cartItems.length
                        ? cartItems.reduce((sum, it) => sum + (Number(it.price) * Number(it.q || 1)), 0)
                        : 0

                const GA4_TO_EEC = {
                    view_item: 'detail',
                    add_to_cart: 'add',
                    begin_checkout: 'checkout',
                    purchase: 'purchase'
                }
                const GA4_TO_FB = {
                    view_item: 'ViewContent',
                    add_to_cart: 'AddToCart',
                    begin_checkout: 'InitiateCheckout',
                    purchase: 'Purchase',
                    add_payment_info: 'AddPaymentInfo',
                    add_to_wishlist: 'AddToWishlist',
                    search: 'Search',
                    start_trial: 'StartTrial',
                    subscribe: 'Subscribe',
                    generate_lead: 'Lead',
                    login: 'CompleteRegistration'
                }

                const contents = cartItems.map((it) => ({
                    id: it._id || it.item_id || it.sku || it.id || '',
                    quantity: 1 //Number(it.q || it.quantity || 1)
                }))
                const numItems = contents.reduce((n, c) => n + Number(c.quantity || 0), 0)

                const baseEcom = {
                    currency: 'HUF',
                    value: 0, //value,
                    ...(event === 'add_payment_info' ? { payment_type: 'Credit Card' } : {}),
                    ...(ecommerce ? ecommerce : {}),
                    ...(cartItems.length ? {
                        items: cartItems.map((it, i) => ({
                            item_id: it._id,
                            item_name: it._id, //it.title,
                            index: i,
                            price: 0, //Number(it.price) || 0,
                            quantity: 1 //Number(it.q || 1)
                        }))
                    } : {})
                }

                const eecAction = GA4_TO_EEC[event]
                const eecMirror = eecAction ? {
                    currencyCode: baseEcom.currency || 'HUF',
                    [eecAction]: {
                        products: cartItems.map(it => ({
                            id: it._id || it.item_id || it.sku || it.id || '',
                            price: 0, //Number(it.price) || 0,
                            quantity: 1 //Number(it.q || 1)
                        }))
                    }
                } : {}

                const fbEventName = GA4_TO_FB[event] || 'CustomEvent'
                const fbParams = {
                    content_type: 'product',
                    contents,
                    value: typeof baseEcom.value === 'number'
                        ? Math.round(baseEcom.value * 100) / 100
                        : 0,
                    currency: baseEcom.currency || 'HUF',
                    ...(numItems ? { num_items: numItems } : {})
                }

                const obj = {
                    event,
                    ...(customer && Object.keys(customer || {}).some(k => customer[k]) ? {
                        customer: {
                            billing_name: customer.invoiceName || null,
                            billing_email: customer.email || req?.user?.email || null,
                            billing_country: customer.invoiceCountry || null,
                            billing_postal_code: customer.invoicePostCode || null,
                            billing_city: customer.invoiceCity || null,
                            billing_street: customer.invoiceAddress || null,
                            billing_vat_number: customer.invoiceTaxNumber || null
                        }
                    } : {}),
                    ecommerce: {
                        ...baseEcom,
                        ...eecMirror
                    },
                    fb: {
                        event_name: fbEventName,
                        params: fbParams
                    }
                }

                push({ obj, req, res })
            } catch (e) {
                console.log(e)
            }
        }
    }
}

export async function firePurchase({ dlName, wapp, req, res }) {
    const query = req.wappRequest.query

    if (query['order']) {
        try {
            function removeFromUrl(url = window.location.href) {
                try {
                    const fullUrl = new URL(url)
                    fullUrl.searchParams.delete('order')
                    return fullUrl.href.split(fullUrl.origin)[1]
                } catch (e) {
                    console.log(e)
                }
            }

            const newUrl = removeFromUrl()
            if (newUrl) {
                window.history.replaceState(window.history.state, undefined, newUrl)
            }
        } catch (e) {
            console.log(e)
        }

        let response = await wapp.requests.send({
            requestName: 'orderFindById',
            args: { _id: query['order'] },
            req,
            res
        })

        response = response['orderFindById'] ? response['orderFindById'] : response

        if (response._id === query['order']) {
            const analytics = getAnalytics({ dlName, wapp })


            analytics.ecommerce({
                event: 'purchase',
                ecommerce: {
                    transaction_id: response?._id
                },
                items: response.cart,
                total: 0, // response.total,
                customer: Object.keys(response).reduce((customer, key) => {
                    if (key.match('invoice')) {
                        const k = key.startsWith('record.') ? key.slice(7) : key
                        customer[k] = response[key]
                    }
                    return customer
                }, {}),
                req, res
            })
        }
    }

}
